<template>
  <div v-if="modalName">
    <component
      :is="componentName"
      :data="modalData"
      @close="onModalClose"
    />
  </div>
</template>

<script lang="ts">
import eventBus from '@/event-bus';
import CompanyType from './CompanyType.vue';
import Confirmation from './Confirmation.vue';
import ErrorModal from './Error.vue';
import MollieError from './MollieError.vue';
import CompanyInfo from './onboarding/CompanyInfo.vue';
import CreateRoster from './onboarding/CreateRoster.vue';
import ImportCustomers from './onboarding/ImportCustomers.vue';
import NewOnboarding from './onboarding/NewOnboarding.vue';
import TryBookingWidget from './onboarding/TryBookingWidget.vue';
import RecurringConfirmation from './RecurringConfirmation.vue';
import RecurringEditOptions from './RecurringEditOptions.vue';
import Upgrade from './Upgrade.vue';
import Warning from './Warning.vue';
import WavyOnboarding from './wavy-onboarding/index.vue';
import Welcome from './welcome/index.vue';

const allowedModalNames = [
  'confirmation',
  'error',
  'warning',
  'welcome',
  'company-type',
  'mollie-error',
  'upgrade',
  'recurring-confirmation',
  'recurring-edit-options',
  'new-onboarding',
  'create-roster',
  'try-booking-widget',
  'import-customers',
  'setup-company-info',
  'wavy-onboarding',
] as const;
export type AllowedModalNames = (typeof allowedModalNames)[number];

export interface GlobalModalData {
  message?: string;
  warning?: string;
}

interface GlobalModalState {
  modalName: AllowedModalNames | null;
  modalData: GlobalModalData;
}

const getDefaultState = (): GlobalModalState => ({
  modalName: null,
  modalData: {
    message: '',
    warning: '',
  },
});

import { defineComponent } from 'vue';

export default defineComponent({
  name: 'TheGlobalModals',
  components: {
    Confirmation,
    ErrorModal,
    Warning,
    Welcome,
    CompanyType,
    MollieError,
    Upgrade,
    RecurringConfirmation,
    RecurringEditOptions,
    NewOnboarding,
    CreateRoster,
    TryBookingWidget,
    ImportCustomers,
    CompanyInfo,
    WavyOnboarding,
  },
  data() {
    return getDefaultState();
  },
  computed: {
    componentName() {
      const name = this.modalName;

      return name
        ? {
            confirmation: 'Confirmation',
            error: 'ErrorModal',
            warning: 'Warning',
            welcome: 'Welcome',
            'company-type': 'CompanyType',
            upgrade: 'Upgrade',
            'mollie-error': 'MollieError',
            'recurring-confirmation': 'RecurringConfirmation',
            'recurring-edit-options': 'RecurringEditOptions',
            'new-onboarding': 'NewOnboarding',
            'create-roster': 'CreateRoster',
            'try-booking-widget': 'TryBookingWidget',
            'import-customers': 'ImportCustomers',
            'setup-company-info': 'CompanyInfo',
            'salonized-assistant': 'Sally',
            'wavy-onboarding': 'WavyOnboarding',
          }[name]
        : null;
    },
  },
  methods: {
    onModalClose() {
      this.reset();
    },
    onModalOpen({
      name,
      options,
    }: {
      name: AllowedModalNames;
      options: unknown;
    }) {
      if (allowedModalNames.includes(name)) {
        this.modalName = name;
        this.modalData = options || {};
      }
    },
    reset() {
      Object.assign(this.$data, getDefaultState());
    },
  },
  created() {
    eventBus.$on('submit-confirmation-modal', this.onModalClose);
    eventBus.$on('open-modal', this.onModalOpen);
    eventBus.$on('after-modal-small-close', this.onModalClose);
  },
  beforeUnmount() {
    eventBus.$off('submit-confirmation-modal', this.onModalClose);
    eventBus.$off('open-modal', this.onModalOpen);
    eventBus.$off('after-modal-small-close', this.onModalClose);
  },
});
</script>
